import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import environment from '@environments/environment';
import { convertQueryParamsForApi } from '@data-access/shared/utils';
import {
  StudyListResponse,
  SiteResponseList,
  StudyListQueryParams,
  StudyDuplicateSearchParams,
  StudySitesParams,
  StudyRegionSitesParams,
} from '../types';

const STUDY_BASE_URL = `${environment.irisApi}/study`;

@Injectable({
  providedIn: 'root',
})
export class StudyService {
  private _http = inject(HttpClient);
  /**
   * Search for duplicate studies by protocol title
   */
  fetchDuplicateStudiesByProtocalTitle(
    params: StudyDuplicateSearchParams,
  ): Observable<{ message: boolean }> {
    return this._http.post<{ message: boolean }>(`${STUDY_BASE_URL}/search/`, {
      ...params,
    });
  }

  /**
   * Get studies with full query parameter support
   */
  getStudies(queryParams: StudyListQueryParams): Observable<StudyListResponse> {
    return this._http.get<StudyListResponse>(`${environment.irisApi}/study/`, {
      params: convertQueryParamsForApi(queryParams),
    });
  }

  /**
   * Get sites for study search with mtr_selection defaulting to true
   */
  getSitesStudySearch(params: StudySitesParams): Observable<SiteResponseList> {
    const { studyId, ...queryParams } = params;
    const apiParams = { ...queryParams };

    // Ensure mtr_selection defaults to true if not specified
    if (apiParams.mtr_selection === undefined) {
      apiParams.mtr_selection = true;
    }

    return this._http.get<SiteResponseList>(
      `${environment.irisApi}/study/${studyId}/site/`,
      {
        params: convertQueryParamsForApi(apiParams),
      },
    );
  }

  /**
   * Get sites for study by region with mtr_selection defaulting to true
   */
  getSitesForStudyByRegion(
    params: StudyRegionSitesParams,
  ): Observable<SiteResponseList> {
    const { studyId, ...queryParams } = params;
    const apiParams = { ...queryParams };

    // Ensure mtr_selection defaults to true if not specified
    if (apiParams.mtr_selection === undefined) {
      apiParams.mtr_selection = true;
    }

    return this._http.get<SiteResponseList>(
      `${environment.irisApi}/study/${studyId}/region_site/`,
      {
        params: convertQueryParamsForApi(apiParams),
      },
    );
  }

  /**
   * Generic method for fetching sites with full query parameter support
   */
  getSitesWithParams(
    params: StudySitesParams,
    endpoint: 'site' | 'region_site' = 'site',
  ): Observable<SiteResponseList> {
    const { studyId, ...queryParams } = params;
    const apiParams = { ...queryParams };

    // Ensure mtr_selection defaults to true if not specified
    if (apiParams.mtr_selection === undefined) {
      apiParams.mtr_selection = true;
    }

    return this._http.get<SiteResponseList>(
      `${environment.irisApi}/study/${studyId}/${endpoint}/`,
      {
        params: convertQueryParamsForApi(apiParams),
      },
    );
  }
}
